import { api, thunks } from "@app/api";
import type { RollbackTarget } from "@app/deployment";
import { call, createSelector } from "@app/fx";
import { defaultEntity, defaultHalHref, extractIdFromLink } from "@app/hal";
import { schema } from "@app/schema";
import { testSecret } from "@app/secrets";
import type { TextVal } from "@app/string-utils";
import type {
  DeployApp,
  DeployAppConfig,
  DeployAppConfigEnv,
  DeployDatabase,
  DeployService,
  LinkResponse,
} from "@app/types";
import type { DeployEndpoint } from "@app/types";
import { parse } from "dotenv";
import type { IdProp } from "starfx";
import { findAppById, rollbackApp, selectAppById, selectApps } from "../app";
import { findDatabaseById, selectDatabases } from "../database";
import { selectEndpointsAsList } from "../endpoint";
import { findServiceById, selectServices } from "../service";

export interface DeployConfigurationResponse {
  id: number;
  env: { [key: string]: string | null } | null;
  _links: {
    resource: LinkResponse;
  };
  _type: "configuration";
}

export const defaultConfigurationResponse = (
  c: Partial<DeployConfigurationResponse> = {},
): DeployConfigurationResponse => {
  return {
    id: 0,
    env: {},
    _links: {
      resource: defaultHalHref(),
    },
    _type: "configuration",
    ...c,
  };
};

export const deserializeAppConfig = (
  resp: DeployConfigurationResponse,
): DeployAppConfig => {
  return {
    id: `${resp.id}`,
    env: resp.env || {},
    appId: extractIdFromLink(resp._links.resource),
  };
};

export const configEnvToStr = (env: DeployAppConfigEnv): string => {
  return Object.keys(env)
    .sort((a, b) => a.localeCompare(b))
    .reduce((acc, key) => {
      let value = String.raw`${env[key]}`;
      const prev = acc ? `${acc}\n` : "";
      if (value.includes("\n")) {
        value = `"${value}"`;
      }
      return `${prev}${key}=${value}`;
    }, "");
};

export const configStrToEnvList = (text: string): TextVal[] => {
  const items: TextVal[] = [];
  const output = parse(text);
  Object.keys(output).forEach((key) => {
    items.push({ key, value: output[key], meta: {} });
  });
  return items;
};

export const configEnvListToEnv = (
  next: TextVal[],
  cur: DeployAppConfigEnv = {},
): DeployAppConfigEnv => {
  const env: DeployAppConfigEnv = {};
  // the way to "remove" env vars from config is to set them as empty
  // so we do that here
  Object.keys(cur).forEach((key) => {
    env[key] = "";
  });

  next.forEach((e) => {
    env[e.key] = e.value;
  });

  return env;
};

// Rollback always pins the exact image via `deploy_image_id`, so a stale
// APTIBLE_DOCKER_IMAGE from the historical config's env must never be
// restored alongside it — that would re-introduce a redundant/conflicting
// image reference. Excluded from both the applied patch and its preview.
const ROLLBACK_EXCLUDED_ENV_KEYS = new Set(["APTIBLE_DOCKER_IMAGE"]);

// Builds an `env` patch that restores `historical` exactly: blanks any
// key not present historically, then sets historical values. Destructive
// by design — see `envRollbackChanges` for a preview.
export const envForRollback = (
  historical: DeployAppConfigEnv,
  current: DeployAppConfigEnv = {},
): DeployAppConfigEnv => {
  const env: DeployAppConfigEnv = {};

  Object.keys(current).forEach((key) => {
    if (ROLLBACK_EXCLUDED_ENV_KEYS.has(key)) return;
    env[key] = "";
  });

  Object.keys(historical).forEach((key) => {
    if (ROLLBACK_EXCLUDED_ENV_KEYS.has(key)) return;
    env[key] = historical[key];
  });

  return env;
};

// Preview of what `envForRollback` would change: removed/changed/restored keys.
export const envRollbackChanges = (
  historical: DeployAppConfigEnv,
  current: DeployAppConfigEnv = {},
): { removed: string[]; changed: string[]; restored: string[] } => {
  const removed: string[] = [];
  const changed: string[] = [];
  const restored: string[] = [];

  Object.keys(current).forEach((key) => {
    if (ROLLBACK_EXCLUDED_ENV_KEYS.has(key)) return;
    if (!(key in historical)) {
      removed.push(key);
    } else if (historical[key] !== current[key]) {
      changed.push(key);
    }
  });

  Object.keys(historical).forEach((key) => {
    if (ROLLBACK_EXCLUDED_ENV_KEYS.has(key)) return;
    if (!(key in current)) {
      restored.push(key);
    }
  });

  return {
    removed: removed.sort((a, b) => a.localeCompare(b)),
    changed: changed.sort((a, b) => a.localeCompare(b)),
    restored: restored.sort((a, b) => a.localeCompare(b)),
  };
};

export const selectAppConfigById = schema.appConfigs.selectById;

export const selectAppConfigByAppId = createSelector(
  schema.appConfigs.selectTable,
  selectAppById,
  (configs, app) =>
    schema.appConfigs.findById(configs, { id: app.currentConfigurationId }),
);

export interface DependencyNode {
  type: string;
  why: string;
  refId: string;
  name: string;
}

export interface AppDependency extends DependencyNode {
  type: "app";
  resource: DeployApp;
}

export interface DatabaseDependency extends DependencyNode {
  type: "database";
  resource: DeployDatabase;
}

export type Dependency = AppDependency | DatabaseDependency;

// Find App and Database dependencies by scanning the configuration for
// known domains used by Databases and Endpoints. Remember to exercise caution
// when using secret values (e.g. DNS resolution will send data to a remote
// server).
function findDependencies(
  config: DeployAppConfig,
  apps: Record<IdProp, DeployApp>,
  dbs: Record<IdProp, DeployDatabase>,
  services: Record<IdProp, DeployService>,
  endpoints: DeployEndpoint[],
): Dependency[] {
  const deps: Dependency[] = [];

  Object.entries(config.env).forEach(([key, value]) => {
    if (value == null) return;

    // Ignore values that do not appear to contain a domain
    const domainMatch = /([a-zA-Z0-9_-]{1,64}\.)+[a-zA-Z]{1,16}/.exec(value);
    if (domainMatch == null) return;
    const domain = domainMatch[0];

    // If the domain looks like a secret, don't bother checking it
    if (testSecret(`${key}=${domain}`)) return;

    // If it looks like one of our database domains (db-<stack>-<id>.), extract
    // the ID and look for the database
    const dbMatch = /db-[a-zA-Z0-9_-]+-([0-9]+)\./.exec(domain);
    if (dbMatch && dbMatch.length > 1) {
      const db = findDatabaseById(dbs, { id: dbMatch[1] });

      if (db) {
        deps.push({
          why: key,
          refId: db.id,
          name: db.handle,
          type: "database",
          resource: db,
        });
        return;
      }
    }

    // There was no matching database, so look for a matching vhost.
    // They could be using a custom domain that matches the endpoint's
    // virtualDomain or they could be using the externalHost that we create.
    const endpoint = endpoints.find(
      (e) => domain === e.virtualDomain || domain === e.externalHost,
    );
    if (endpoint == null) return;
    const service = findServiceById(services, { id: endpoint.serviceId });

    // The matching endpoint could belong to an App or Database service
    if (service.appId) {
      const app = findAppById(apps, { id: service.appId });

      deps.push({
        why: key,
        refId: app.id,
        name: app.handle,
        type: "app",
        resource: app,
      });
    } else if (service.databaseId) {
      const db = findDatabaseById(dbs, { id: service.databaseId });

      deps.push({
        why: key,
        refId: db.id,
        name: db.handle,
        type: "database",
        resource: db,
      });
    }
  });

  return deps;
}

export const selectDependencies = createSelector(
  selectAppConfigByAppId,
  selectApps,
  selectDatabases,
  selectServices,
  selectEndpointsAsList,
  findDependencies,
);

export const selectDependenciesByType = createSelector(
  selectDependencies,
  (deps) => {
    const depGroups: { app: AppDependency[]; database: DatabaseDependency[] } =
      {
        app: [],
        database: [],
      };

    deps.forEach((dep) => {
      switch (dep.type) {
        case "app":
          depGroups.app.push(dep);
          break;
        case "database":
          depGroups.database.push(dep);
          break;
      }
    });

    return depGroups;
  },
);

export const fetchConfiguration = api.get<
  { id: string; withSensitiveExtras?: boolean },
  DeployConfigurationResponse
>("/configurations/:id");

interface RollbackDeploymentProps {
  appId: string;
  /** From `rollbackTargetFor(deployment)`; `null` = can't be rolled back to. */
  target: RollbackTarget | null;
  configurationId: string;
  /** App's current configuration id; only needed when restoreEnv is true. */
  currentConfigurationId?: string;
  restoreEnv: boolean;
}

// Fetches target/current config (if restoring env), then deploys the rollback.
// Lives here (not `../app`) to avoid an app <-> configuration import cycle.
export const rollbackDeployment = thunks.create<RollbackDeploymentProps>(
  "rollback-deployment",
  function* (ctx, next) {
    yield* schema.update(schema.loaders.start({ id: ctx.key }));

    const { appId, target, configurationId, currentConfigurationId } =
      ctx.payload;

    if (!target) {
      yield* schema.update(
        schema.loaders.error({
          id: ctx.key,
          message: "This deployment cannot be rolled back to.",
        }),
      );
      return;
    }

    let env: DeployAppConfigEnv | undefined;

    if (ctx.payload.restoreEnv) {
      const targetCtx = yield* call(() =>
        fetchConfiguration.run(
          fetchConfiguration({
            id: configurationId,
            withSensitiveExtras: true,
          }),
        ),
      );
      if (!targetCtx.json.ok) {
        yield* schema.update(
          schema.loaders.error({
            id: ctx.key,
            message: targetCtx.json.error.message,
          }),
        );
        return;
      }

      let currentEnv: DeployAppConfigEnv = {};
      if (currentConfigurationId) {
        const currentCtx = yield* call(() =>
          fetchConfiguration.run(
            fetchConfiguration({
              id: currentConfigurationId,
              withSensitiveExtras: true,
            }),
          ),
        );
        if (!currentCtx.json.ok) {
          yield* schema.update(
            schema.loaders.error({
              id: ctx.key,
              message: currentCtx.json.error.message,
            }),
          );
          return;
        }
        currentEnv = currentCtx.json.value.env || {};
      }

      env = envForRollback(targetCtx.json.value.env || {}, currentEnv);
    }

    const rollbackCtx = yield* call(() =>
      rollbackApp.run(rollbackApp({ appId, target, env })),
    );
    if (!rollbackCtx.json.ok) {
      yield* schema.update(
        schema.loaders.error({
          id: ctx.key,
          message: rollbackCtx.json.error.message,
        }),
      );
      return;
    }

    const opId = `${rollbackCtx.json.value.id}`;
    yield* next();

    ctx.json = rollbackCtx.json;
    yield* schema.update(
      schema.loaders.success({
        id: ctx.key,
        meta: { opId },
      }),
    );
  },
);

export const appConfigEntities = {
  configuration: defaultEntity({
    id: "configuration",
    deserialize: deserializeAppConfig,
    save: schema.appConfigs.add,
  }),
};
